import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { transactionService } from '../../services/transactionService';
import { customerService } from '../../services/customerService';
import { inventoryService } from '../../services/inventoryService';
import { workOrderService } from '../../services/workOrderService';
import { FiPlus, FiEye, FiEdit, FiDollarSign, FiTrash2, FiCheck } from 'react-icons/fi';

const TransactionList = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [transactionsData, customersData, workOrdersData] = await Promise.all([
        transactionService.getAll(),
        customerService.getAll(),
        workOrderService.getAll()
      ]);
      setTransactions(transactionsData || []);
      setCustomers(customersData || []);
      setWorkOrders(workOrdersData || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (id) => {
    if (!id) return '-';
    const customer = customers.find(c => c.id === id);
    return customer ? customer.name : '-';
  };

  const getWOStatusLabel = (status) => {
    const labels = {
      'pending': 'Order Masuk',
      'in-progress': 'In Progress',
      'completed': 'Selesai',
      'delivered': 'Dikirim',
      'cancelled': 'Dibatalkan'
    };
    return labels[status] || status || '-';
  };

  const getWOStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'delivered': 'bg-purple-100 text-purple-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getWOStatus = (workOrderId) => {
    if (!workOrderId) return null;
    const wo = workOrders.find(w => w.id === workOrderId);
    return wo ? wo.status : null;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'paid': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'failed': 'bg-red-100 text-red-800',
      'refunded': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      'paid': 'Lunas',
      'pending': 'Pending',
      'failed': 'Gagal',
      'refunded': 'Dikembalikan'
    };
    return labels[status] || status;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'cash': 'Tunai',
      'transfer': 'Transfer Bank',
      'qris': 'QRIS',
      'credit': 'Kredit'
    };
    return labels[method] || method;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Hapus transaksi ini? Stock akan dikembalikan.')) {
      try {
        const transaction = await transactionService.getById(id);
        if (transaction && transaction.paymentStatus === 'paid') {
          const items = transaction.items || [];
          for (const item of items) {
            try {
              const inventory = await inventoryService.getById(item.inventoryId);
              if (inventory) {
                await inventoryService.addStock(
                  item.inventoryId,
                  item.quantity,
                  `Stock returned from deleted transaction ${id}`
                );
              }
            } catch (error) {
              console.error('Error returning stock:', error);
            }
          }
        }
        await transactionService.delete(id);
        await loadData();
      } catch (error) {
        alert('Error deleting transaction: ' + error.message);
      }
    }
  };

  const handlePay = async (id) => {
    if (window.confirm('Konfirmasi pembayaran untuk transaksi ini?')) {
      try {
        await transactionService.updatePaymentStatus(id, 'paid');
        await loadData();
      } catch (error) {
        alert('Error: ' + error.message);
      }
    }
  };

  // 🔥 EDIT — NAVIGATE KE HALAMAN EDIT (BUKAN MODAL)
  const handleEdit = async (id) => {
    try {
      const transaction = await transactionService.getById(id);
      if (transaction && transaction.paymentStatus === 'paid') {
        alert('⚠️ Transaksi sudah lunas dan tidak bisa diedit!');
        return;
      }
      navigate(`/transactions/edit/${id}`);
    } catch (error) {
      console.error('Error checking transaction:', error);
      alert('Gagal memuat data transaksi');
    }
  };

  const filteredTransactions = Array.isArray(transactions)
    ? transactions.map(t => ({
        ...t,
        workOrderStatus: getWOStatus(t.workOrderId)
      }))
    .filter(t => {
        const customerName = getCustomerName(t.customerId);
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          (t.workOrderId && t.workOrderId.toLowerCase().includes(searchLower)) ||
          (customerName && customerName.toLowerCase().includes(searchLower));
        const matchesFilter = filter === 'all' || t.paymentStatus === filter;
        return matchesSearch && matchesFilter;
      })
    : [];

  const stats = {
    totalTransactions: Array.isArray(transactions) ? transactions.length : 0,
    totalRevenue: Array.isArray(transactions)
      ? transactions.filter(t => t.paymentStatus === 'paid')
          .reduce((sum, t) => sum + (t.total || 0), 0)
      : 0,
    totalPending: Array.isArray(transactions)
      ? transactions.filter(t => t.paymentStatus === 'pending')
          .reduce((sum, t) => sum + (t.total || 0), 0)
      : 0,
    paidCount: Array.isArray(transactions)
      ? transactions.filter(t => t.paymentStatus === 'paid').length
      : 0,
    pendingCount: Array.isArray(transactions)
      ? transactions.filter(t => t.paymentStatus === 'pending').length
      : 0
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Loading transactions...</p>
      </div>
    );
  }

  return (
    <div>
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Kasir - Transaksi</h1>
          {stats.pendingCount > 0 && (
            <span className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-full animate-pulse">
              🔔 {stats.pendingCount} tagihan pending
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => {
              setFilter('pending');
              setTimeout(() => {
                document.getElementById('pending-list')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            title="Bayar Work Order yang sudah selesai"
          >
            <FiDollarSign size={18} />
            <span>Bayar WO</span>
            {stats.pendingCount > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                {stats.pendingCount}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/transactions/new')}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            title="Transaksi manual"
          >
            <FiPlus size={18} />
            <span>Transaksi Manual</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[var(--bg-primary)] p-4 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
          <p className="text-sm text-[var(--text-secondary)]">Total Transaksi</p>
          <p className="text-2xl font-bold text-[var(--text-primary)]">{stats.totalTransactions}</p>
        </div>
        <div className="bg-[var(--bg-primary)] p-4 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
          <p className="text-sm text-[var(--text-secondary)]">Total Revenue</p>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalRevenue)}</p>
        </div>
        <div className="bg-[var(--bg-primary)] p-4 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
          <p className="text-sm text-[var(--text-secondary)]">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">{formatCurrency(stats.totalPending)}</p>
        </div>
        <div className="bg-[var(--bg-primary)] p-4 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
          <p className="text-sm text-[var(--text-secondary)]">Completion Rate</p>
          <p className="text-2xl font-bold text-blue-600">
            {stats.totalTransactions > 0
              ? Math.round((stats.paidCount / stats.totalTransactions) * 100)
              : 0}%
          </p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <input
          type="text"
          placeholder="Search by WO Number or Customer..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      {/* Transactions Table */}
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[var(--bg-secondary)]">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-secondary)]">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-secondary)]">WO Number</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-secondary)]">Customer</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-secondary)]">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-secondary)]">Total</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-secondary)]">Method</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-secondary)]">Status WO</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--text-secondary)]">Payment</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-[var(--text-secondary)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-6 text-center text-[var(--text-secondary)]">
                    <FiDollarSign size={18} className="mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No transactions found</p>
                    <p className="text-sm">Klik "Bayar WO" untuk melihat tagihan, atau "Transaksi Manual" untuk transaksi non-WO</p>
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((t) => (
                  <tr
                    key={t.id}
                    id={t.paymentStatus === 'pending' ? 'pending-list' : ''}
                    className="border-t border-[var(--border-color)] hover:bg-[var(--bg-secondary)] transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-mono text-[var(--text-primary)]">
                      {t.id || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-primary)]">
                      {t.workOrderId || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                      {getCustomerName(t.customerId)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                      {formatDate(t.date)}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-[var(--text-primary)]">
                      {formatCurrency(t.total)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                      {getPaymentMethodLabel(t.paymentMethod)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getWOStatusColor(t.workOrderStatus)}`}>
                        {getWOStatusLabel(t.workOrderStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPaymentStatusColor(t.paymentStatus)}`}>
                        {getPaymentStatusLabel(t.paymentStatus)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        {/* View Detail */}
                        <button
                          onClick={() => navigate(`/transactions/${t.id}`)}
                          className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <FiEye size={18} />
                        </button>
                        
                        {/* 🔥 EDIT — NAVIGATE KE HALAMAN EDIT */}
                        {(!t.paymentStatus || t.paymentStatus === 'pending') && (
                          <button
                            onClick={() => handleEdit(t.id)}
                            className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit Transaksi"
                          >
                            <FiEdit size={18} />
                          </button>
                        )}
                        
                        {/* Pay */}
                        {t.paymentStatus === 'pending' && (
                          <button
                            onClick={() => handlePay(t.id)}
                            className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                            title="Bayar"
                          >
                            <FiCheck size={18} />
                          </button>
                        )}
                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(t.id)}
                          className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TransactionList;